// Iter-190 — Payroll settings (Moroccan CNSS rates + cap). CEO only.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Loader2, Wallet, RefreshCw } from "lucide-react";

export default function PayrollSettingsBlock() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [fxLastUpdate, setFxLastUpdate] = useState(null);
  const [form, setForm] = useState({
    payroll_cnss_plafond_mad: 6000,
    // Iter-237 — itemized employer charges
    payroll_amo_patronal_pct: 4.11,
    payroll_alloc_fam_pct: 6.40,
    payroll_lt_pct: 7.93,
    payroll_ct_pct: 1.05,
    payroll_tfp_pct: 1.60,
    // Iter-191e — FX rates used to normalize foreign-currency missions to MAD for
    // profitability tracking. Defaults are sensible (Bank Al-Maghrib mid-range).
    payroll_fx_eur_to_mad: 10.8,
    payroll_fx_usd_to_mad: 10.0,
    // Iter-191g — auto-refresh toggle (default: enabled).
    auto_fx_update_enabled: true,
    // Iter-236 — employee-side params for brut ↔ net simulation (+ IR).
    payroll_cnss_salarial_pct: 4.48,
    payroll_amo_salarial_pct: 2.26,
    payroll_frais_pro_pct: 35,
    payroll_frais_pro_plafond_mensuel: 2916.67,
  });
  const DEFAULT_IR_BAREME = [
    { min: 0, rate_pct: 0, deduction: 0 },
    { min: 3333.34, rate_pct: 10, deduction: 333.33 },
    { min: 5000.01, rate_pct: 20, deduction: 833.33 },
    { min: 6666.68, rate_pct: 30, deduction: 1500 },
    { min: 8333.34, rate_pct: 34, deduction: 1833.33 },
    { min: 15000.01, rate_pct: 37, deduction: 2283.33 },
  ];
  const [irBareme, setIrBareme] = useState(DEFAULT_IR_BAREME);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/settings");
        if (cancel) return;
        const d = r.data || {};
        setForm({
          payroll_cnss_plafond_mad: d.payroll_cnss_plafond_mad ?? 6000,
          payroll_amo_patronal_pct: d.payroll_amo_patronal_pct ?? 4.11,
          payroll_alloc_fam_pct: d.payroll_alloc_fam_pct ?? 6.40,
          payroll_lt_pct: d.payroll_lt_pct ?? 7.93,
          payroll_ct_pct: d.payroll_ct_pct ?? 1.05,
          payroll_tfp_pct: d.payroll_tfp_pct ?? 1.60,
          payroll_fx_eur_to_mad: d.payroll_fx_eur_to_mad ?? 10.8,
          payroll_fx_usd_to_mad: d.payroll_fx_usd_to_mad ?? 10.0,
          auto_fx_update_enabled: d.auto_fx_update_enabled !== false,  // default true
          payroll_cnss_salarial_pct: d.payroll_cnss_salarial_pct ?? 4.48,
          payroll_amo_salarial_pct: d.payroll_amo_salarial_pct ?? 2.26,
          payroll_frais_pro_pct: d.payroll_frais_pro_pct ?? 35,
          payroll_frais_pro_plafond_mensuel: d.payroll_frais_pro_plafond_mensuel ?? 2916.67,
        });
        setIrBareme(Array.isArray(d.payroll_ir_bareme) && d.payroll_ir_bareme.length ? d.payroll_ir_bareme : DEFAULT_IR_BAREME);
        setFxLastUpdate(d.fx_last_update_at || null);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur de chargement");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        payroll_cnss_plafond_mad: parseFloat(form.payroll_cnss_plafond_mad),
        payroll_amo_patronal_pct: parseFloat(form.payroll_amo_patronal_pct),
        payroll_alloc_fam_pct: parseFloat(form.payroll_alloc_fam_pct),
        payroll_lt_pct: parseFloat(form.payroll_lt_pct),
        payroll_ct_pct: parseFloat(form.payroll_ct_pct),
        payroll_tfp_pct: parseFloat(form.payroll_tfp_pct),
        payroll_fx_eur_to_mad: parseFloat(form.payroll_fx_eur_to_mad),
        payroll_fx_usd_to_mad: parseFloat(form.payroll_fx_usd_to_mad),
        auto_fx_update_enabled: !!form.auto_fx_update_enabled,
        payroll_cnss_salarial_pct: parseFloat(form.payroll_cnss_salarial_pct),
        payroll_amo_salarial_pct: parseFloat(form.payroll_amo_salarial_pct),
        payroll_frais_pro_pct: parseFloat(form.payroll_frais_pro_pct),
        payroll_frais_pro_plafond_mensuel: parseFloat(form.payroll_frais_pro_plafond_mensuel),
        payroll_ir_bareme: irBareme.map((r) => ({
          min: parseFloat(r.min) || 0,
          rate_pct: parseFloat(r.rate_pct) || 0,
          deduction: parseFloat(r.deduction) || 0,
        })),
      };
      await api.put("/settings", payload);
      toast.success("Paramètres masse salariale enregistrés");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Iter-191g — manual FX refresh (CEO only).
  const refreshFxNow = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/settings/fx/refresh");
      const eur = r.data?.eur_to_mad;
      const usd = r.data?.usd_to_mad;
      if (eur || usd) {
        setForm((f) => ({
          ...f,
          payroll_fx_eur_to_mad: eur ?? f.payroll_fx_eur_to_mad,
          payroll_fx_usd_to_mad: usd ?? f.payroll_fx_usd_to_mad,
        }));
        setFxLastUpdate(r.data?.fetched_at || new Date().toISOString());
        toast.success(`Taux actualisés: 1 EUR = ${eur} MAD, 1 USD = ${usd} MAD`);
      } else {
        toast.error("Aucun taux récupéré");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Echec de la récupération des taux");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-4 mb-6" data-testid="payroll-settings-block">
      <div className="flex items-center gap-3">
        <Wallet size={18} className="text-zinc-700" />
        <h2 className="font-heading font-bold text-lg">Masse salariale · paramètres CNSS (Maroc)</h2>
      </div>
      <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
        Utilisé pour calculer le CJM des salariés marocains.
        Coût mensuel = Brut + (Brut × full %) + (min(Brut, plafond) × plafonné %).
        CJM = Coût mensuel / jours ouvrés du mois.
      </p>
      {loading ? (
        <div className="text-sm text-zinc-500 py-4"><Loader2 size={14} className="inline animate-spin mr-2" />Chargement…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="payroll-plafond">Plafond CNSS (MAD)</Label>
            <Input
              id="payroll-plafond"
              data-testid="payroll-plafond"
              type="number" min="0" step="100"
              value={form.payroll_cnss_plafond_mad}
              onChange={(e) => setForm({ ...form, payroll_cnss_plafond_mad: e.target.value })}
            />
            <div className="text-[10px] text-zinc-500 font-mono mt-1">Valeur officielle 2026 : 6 000 MAD</div>
          </div>
          {/* Iter-237 — itemized employer charges (replaces the old flat rates). */}
          <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="payroll-employer-itemized">
            <div>
              <Label>AMO patronale (%)</Label>
              <Input data-testid="payroll-amo-patronal" type="number" step="0.01" value={form.payroll_amo_patronal_pct}
                onChange={(e) => setForm({ ...form, payroll_amo_patronal_pct: e.target.value })} />
              <div className="text-[10px] text-zinc-500 mt-1">Brut total · déf. 4,11%</div>
            </div>
            <div>
              <Label>Alloc. familiales (%)</Label>
              <Input data-testid="payroll-alloc-fam" type="number" step="0.01" value={form.payroll_alloc_fam_pct}
                onChange={(e) => setForm({ ...form, payroll_alloc_fam_pct: e.target.value })} />
              <div className="text-[10px] text-zinc-500 mt-1">Brut total · déf. 6,40%</div>
            </div>
            <div>
              <Label>Long terme (%)</Label>
              <Input data-testid="payroll-lt" type="number" step="0.01" value={form.payroll_lt_pct}
                onChange={(e) => setForm({ ...form, payroll_lt_pct: e.target.value })} />
              <div className="text-[10px] text-zinc-500 mt-1">Plafonné · déf. 7,93%</div>
            </div>
            <div>
              <Label>Court terme (%)</Label>
              <Input data-testid="payroll-ct" type="number" step="0.01" value={form.payroll_ct_pct}
                onChange={(e) => setForm({ ...form, payroll_ct_pct: e.target.value })} />
              <div className="text-[10px] text-zinc-500 mt-1">Plafonné · déf. 1,05%</div>
            </div>
            <div>
              <Label>TFP (%)</Label>
              <Input data-testid="payroll-tfp" type="number" step="0.01" value={form.payroll_tfp_pct}
                onChange={(e) => setForm({ ...form, payroll_tfp_pct: e.target.value })} />
              <div className="text-[10px] text-zinc-500 mt-1">Brut total · déf. 1,60%</div>
            </div>
            <div className="col-span-2 sm:col-span-5 text-[11px] font-mono text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2" data-testid="payroll-employer-summary">
              {(() => {
                const full = (parseFloat(form.payroll_amo_patronal_pct) || 0) + (parseFloat(form.payroll_alloc_fam_pct) || 0) + (parseFloat(form.payroll_tfp_pct) || 0);
                const capped = (parseFloat(form.payroll_lt_pct) || 0) + (parseFloat(form.payroll_ct_pct) || 0);
                const plaf = parseFloat(form.payroll_cnss_plafond_mad) || 6000;
                const ex = 10000;
                const charges = ex * full / 100 + Math.min(ex, plaf) * capped / 100;
                return `Base pleine : ${full.toFixed(2)}% · Base plafonnée : ${capped.toFixed(2)}% — Exemple brut 10 000 MAD → charges patronales ${charges.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} MAD (≈ ${(charges / ex * 100).toFixed(2)}%)`;
              })()}
            </div>
          </div>
        </div>
      )}
      {!loading && (
        <div className="pt-4 mt-2 border-t border-zinc-200" data-testid="payroll-ir-block">
          <h3 className="font-heading font-semibold text-sm">Cotisations salariales & IR (simulation brut ↔ net)</h3>
          <p className="text-[11px] text-zinc-500 mt-0.5 max-w-2xl">
            Utilisé dans le dialog Rémunération pour calculer dynamiquement le net depuis le brut (et inversement).
            Net = Brut − CNSS salariale (plafonnée) − AMO − IR. IR = SNI × taux − somme à déduire.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
            <div>
              <Label>Taux CNSS salarial (%)</Label>
              <Input data-testid="payroll-cnss-salarial" type="number" step="0.01" value={form.payroll_cnss_salarial_pct}
                onChange={(e) => setForm({ ...form, payroll_cnss_salarial_pct: e.target.value })} />
              <p className="text-[10px] text-zinc-500 mt-1">Défaut : 4,48% (plafonné au plafond CNSS)</p>
            </div>
            <div>
              <Label>Taux AMO salarial (%)</Label>
              <Input data-testid="payroll-amo-salarial" type="number" step="0.01" value={form.payroll_amo_salarial_pct}
                onChange={(e) => setForm({ ...form, payroll_amo_salarial_pct: e.target.value })} />
              <p className="text-[10px] text-zinc-500 mt-1">Défaut : 2,26% (non plafonné)</p>
            </div>
            <div>
              <Label>Frais professionnels (%)</Label>
              <Input data-testid="payroll-frais-pro" type="number" step="0.5" value={form.payroll_frais_pro_pct}
                onChange={(e) => setForm({ ...form, payroll_frais_pro_pct: e.target.value })} />
              <p className="text-[10px] text-zinc-500 mt-1">Abattement — défaut : 35%</p>
            </div>
            <div>
              <Label>Plafond frais pro (MAD/mois)</Label>
              <Input data-testid="payroll-frais-pro-plafond" type="number" step="1" value={form.payroll_frais_pro_plafond_mensuel}
                onChange={(e) => setForm({ ...form, payroll_frais_pro_plafond_mensuel: e.target.value })} />
              <p className="text-[10px] text-zinc-500 mt-1">Défaut : 2 916,67 (35 000/an)</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <Label>Barème IR mensuel (tranches)</Label>
              <button
                onClick={() => setIrBareme(DEFAULT_IR_BAREME)}
                data-testid="payroll-ir-reset"
                className="text-[11px] text-zinc-500 hover:text-zinc-900 underline"
              >
                Réinitialiser (barème officiel 2026)
              </button>
            </div>
            <div className="mt-2 border border-zinc-200 rounded-md overflow-hidden">
              <div className="grid grid-cols-10 gap-2 px-3 py-1.5 bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                <div className="col-span-3">SNI à partir de (MAD)</div>
                <div className="col-span-3">Taux (%)</div>
                <div className="col-span-3">Somme à déduire (MAD)</div>
                <div></div>
              </div>
              {irBareme.map((row, i) => (
                <div key={i} className="grid grid-cols-10 gap-2 px-3 py-1.5 border-t border-zinc-100 items-center">
                  <Input className="col-span-3 h-8 text-xs font-mono" type="number" step="0.01" value={row.min}
                    data-testid={`payroll-ir-min-${i}`}
                    onChange={(e) => setIrBareme(irBareme.map((r, j) => j === i ? { ...r, min: e.target.value } : r))} />
                  <Input className="col-span-3 h-8 text-xs font-mono" type="number" step="0.5" value={row.rate_pct}
                    data-testid={`payroll-ir-rate-${i}`}
                    onChange={(e) => setIrBareme(irBareme.map((r, j) => j === i ? { ...r, rate_pct: e.target.value } : r))} />
                  <Input className="col-span-3 h-8 text-xs font-mono" type="number" step="0.01" value={row.deduction}
                    data-testid={`payroll-ir-deduction-${i}`}
                    onChange={(e) => setIrBareme(irBareme.map((r, j) => j === i ? { ...r, deduction: e.target.value } : r))} />
                  <button onClick={() => setIrBareme(irBareme.filter((_, j) => j !== i))}
                    data-testid={`payroll-ir-remove-${i}`}
                    className="text-zinc-400 hover:text-red-600 text-xs">✕</button>
                </div>
              ))}
              <button
                onClick={() => setIrBareme([...irBareme, { min: 0, rate_pct: 0, deduction: 0 }])}
                data-testid="payroll-ir-add"
                className="w-full px-3 py-1.5 text-[11px] text-zinc-500 hover:bg-zinc-50 border-t border-zinc-100 text-left"
              >
                + Ajouter une tranche
              </button>
            </div>
          </div>
        </div>
      )}
      {!loading && (
        <div className="pt-4 mt-2 border-t border-zinc-200">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <h3 className="font-heading font-semibold text-sm">Taux de change pour la rentabilité</h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                {fxLastUpdate ? (
                  <>Dernière mise à jour automatique : <span className="font-mono">{new Date(fxLastUpdate).toLocaleString("fr-FR")}</span></>
                ) : (
                  <>Aucune mise à jour automatique encore exécutée.</>
                )}
              </p>
            </div>
            <button
              onClick={refreshFxNow}
              disabled={refreshing}
              data-testid="payroll-fx-refresh-now"
              className="inline-flex items-center gap-1.5 px-3 h-9 bg-white border border-zinc-200 text-zinc-700 text-xs rounded-md hover:bg-zinc-50 disabled:opacity-60"
              title="Recharger les taux depuis l'API publique (open.er-api.com, dérivé BCE)"
            >
              {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Actualiser maintenant
            </button>
          </div>
          <p className="text-xs text-zinc-500 mb-3 max-w-2xl leading-relaxed">
            Les missions en EUR ou USD ont leur chiffre d'affaires converti en MAD avec ces taux pour calculer la marge (le CJM des collaborateurs est en MAD). Une actualisation automatique a lieu le 1er de chaque mois (source : open.er-api.com / BCE).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="payroll-fx-eur">Taux 1 EUR → MAD</Label>
              <Input
                id="payroll-fx-eur"
                data-testid="payroll-fx-eur"
                type="number" min="0" step="0.0001"
                value={form.payroll_fx_eur_to_mad}
                onChange={(e) => setForm({ ...form, payroll_fx_eur_to_mad: e.target.value })}
              />
              <div className="text-[10px] text-zinc-500 font-mono mt-1">Valeur indicative Bank Al-Maghrib : ~10.8 MAD</div>
            </div>
            <div>
              <Label htmlFor="payroll-fx-usd">Taux 1 USD → MAD</Label>
              <Input
                id="payroll-fx-usd"
                data-testid="payroll-fx-usd"
                type="number" min="0" step="0.0001"
                value={form.payroll_fx_usd_to_mad}
                onChange={(e) => setForm({ ...form, payroll_fx_usd_to_mad: e.target.value })}
              />
              <div className="text-[10px] text-zinc-500 font-mono mt-1">Valeur indicative : ~10.0 MAD</div>
            </div>
          </div>
          <label className="mt-3 flex items-start gap-2 text-sm" data-testid="payroll-fx-auto-toggle-wrap">
            <input
              type="checkbox"
              checked={!!form.auto_fx_update_enabled}
              onChange={(e) => setForm({ ...form, auto_fx_update_enabled: e.target.checked })}
              className="mt-0.5"
              data-testid="payroll-fx-auto-toggle"
            />
            <span className="leading-snug">
              <strong>Actualiser automatiquement chaque mois</strong>
              <span className="text-zinc-500 text-xs block">
                Les taux EUR→MAD et USD→MAD seront récupérés le 1er de chaque mois à 02:15 UTC. Décocher pour utiliser uniquement les valeurs saisies manuellement.
              </span>
            </span>
          </label>
        </div>
      )}
      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving || loading}
          data-testid="payroll-save"
          className="inline-flex items-center gap-1.5 px-3 h-9 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600 disabled:opacity-60"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}
